import { type Op } from 'quill-delta';

import {
  type CustomNumberFormat,
  type CustomNumberFormatJSON,
  type MutableCustomNumberFormat,
} from './custom-number-format';

export enum DateFormat {
  DEFAULT = 'default',
  FULL = 'full',
  MONTH_DAY_YEAR = 'month-day-year',
  DAY_MONTH_YEAR = 'day-month-year',
  YEAR_MONTH_DAY = 'year-month-day',
}
export const DateFormatPossibleValues = Object.values(DateFormat);

export enum TimeFormat {
  TWELVE_HOUR = '12',
  TWENTY_FOUR_HOUR = '24',
}
export const TimeFormatPossibleValues = Object.values(TimeFormat);

export enum PageSubType {
  PAGE = 'PAGE',
  LIVE = 'LIVE',
}

export enum MediaFormat {
  AUTOFILL = 'Autofill',
  THUMBNAIL = 'Thumbnail',
}

// TODO: remove AUTO and MACRO. Use it properly to define the type of
// parent content. At which point, we can begin to deprecate `settingIsParentPageSourceDatabase`
export enum ParentPageType {
  PAGE = 'PAGE',
  AUTO = 'AUTO',
  MACRO = 'MACRO',
}

export enum ParentPageOption {
  DYNAMIC = 'dynamic',
  FIXED = 'fixed',
}

export enum FieldType {
  Text = 'text',
  AiSummary = 'aiSummary',
  Number = 'number',
  Date = 'date',
  Checkbox = 'checkbox',
  Select = 'select',
  User = 'user',
  Attachment = 'attachment',
  PageLink = 'page',
  PageLabels = 'pageLabels',
  PageStatus = 'pageStatus',
  PageDetails = 'pageDetails',
  Hyperlink = 'hyperlink',
  EntryLink = 'entryLink',
  EntryDetails = 'entryDetails',
  Backlink = 'backlink',
  JiraIssue = 'jiraIssue',
  JiraIssueDetails = 'jiraIssueDetails',
}

// outdated, eventually remove
export enum DisplayedDetailType {
  CREATED_AT = 'createdAt',
  CREATED_BY = 'createdBy',
  LAST_UPDATED_AT = 'lastUpdatedAt',
  LAST_UPDATED_BY = 'lastUpdatedBy',
  EXCERPT_CONTENT = 'excerptContent',
  VERSION_NUMBER = 'versionNumber',
}

export enum ContentFieldToDisplayedDetailType {
  CREATED_AT = 'createdAt',
  CREATED_BY = 'createdBy',
  UPDATED_AT = 'updatedAt',
  UPDATED_BY = 'updatedBy',
  EXCERPT = 'excerpt',
  VERSION_NUMBER = 'versionNumber',
}

export enum BacklinkDisplayType {
  AS_LINK = 'asLink',
  AS_NUMBER = 'asNumber',
}
export enum NumberFormat {
  NUMBER = 'number',
  PERCENT = 'percent',
  CUSTOM = 'custom',
  DOLLAR = 'dollar',
  EURO = 'euro',
  CURRENCY = 'currency',
}

export enum YNumberFormat {
  NUMBER = 'n',
  PERCENT = 'p',
  DOLLAR = '$',
  EURO = 'e',
  CUSTOM = 'c',
  CURRENCY = 'cy',
}
export const YNumberFormatPossibleValues = Object.values(YNumberFormat);

export enum YDisplayedDetailType {
  CREATED_AT = 'ca',
  CREATED_BY = 'cb',
  LAST_UPDATED_AT = 'lua',
  LAST_UPDATED_BY = 'lub',
  EXCERPT_CONTENT = 'ec',
  VERSION_NUMBER = 'vn',
}
export const YDisplayedDetailTypePossibleValues = Object.values(YDisplayedDetailType);

export enum YBacklinkDisplayType {
  AS_LINK = 'al',
  AS_NUMBER = 'an',
}
export const YBacklinkDisplayTypePossibleValues = Object.values(YBacklinkDisplayType);

export const NumberFormatMap: Record<NumberFormat, YNumberFormat> = {
  [NumberFormat.NUMBER]: YNumberFormat.NUMBER,
  [NumberFormat.PERCENT]: YNumberFormat.PERCENT,
  [NumberFormat.DOLLAR]: YNumberFormat.DOLLAR,
  [NumberFormat.EURO]: YNumberFormat.EURO,
  [NumberFormat.CUSTOM]: YNumberFormat.CUSTOM,
  [NumberFormat.CURRENCY]: YNumberFormat.CURRENCY,
};

export const YNumberFormatMap: Record<YNumberFormat, NumberFormat> = {
  [YNumberFormat.NUMBER]: NumberFormat.NUMBER,
  [YNumberFormat.PERCENT]: NumberFormat.PERCENT,
  [YNumberFormat.DOLLAR]: NumberFormat.DOLLAR,
  [YNumberFormat.EURO]: NumberFormat.EURO,
  [YNumberFormat.CUSTOM]: NumberFormat.CUSTOM,
  [YNumberFormat.CURRENCY]: NumberFormat.CURRENCY,
};

export const ContentFieldToDisplayedDetailTypeMap: Record<string, DisplayedDetailType> = {
  [ContentFieldToDisplayedDetailType.CREATED_AT]: DisplayedDetailType.CREATED_AT,
  [ContentFieldToDisplayedDetailType.CREATED_BY]: DisplayedDetailType.CREATED_BY,
  [ContentFieldToDisplayedDetailType.UPDATED_AT]: DisplayedDetailType.LAST_UPDATED_AT,
  [ContentFieldToDisplayedDetailType.UPDATED_BY]: DisplayedDetailType.LAST_UPDATED_BY,
  [ContentFieldToDisplayedDetailType.EXCERPT]: DisplayedDetailType.EXCERPT_CONTENT,
  [ContentFieldToDisplayedDetailType.VERSION_NUMBER]: DisplayedDetailType.VERSION_NUMBER,
};

export const DisplayedDetailTypeToContentFieldMap: Record<DisplayedDetailType, string> = {
  [DisplayedDetailType.CREATED_AT]: ContentFieldToDisplayedDetailType.CREATED_AT,
  [DisplayedDetailType.CREATED_BY]: ContentFieldToDisplayedDetailType.CREATED_BY,
  [DisplayedDetailType.LAST_UPDATED_AT]: ContentFieldToDisplayedDetailType.UPDATED_AT,
  [DisplayedDetailType.LAST_UPDATED_BY]: ContentFieldToDisplayedDetailType.UPDATED_BY,
  [DisplayedDetailType.EXCERPT_CONTENT]: ContentFieldToDisplayedDetailType.EXCERPT,
  [DisplayedDetailType.VERSION_NUMBER]: ContentFieldToDisplayedDetailType.VERSION_NUMBER,
};

export const DisplayedDetailTypeMap: Record<DisplayedDetailType, YDisplayedDetailType> = {
  [DisplayedDetailType.CREATED_AT]: YDisplayedDetailType.CREATED_AT,
  [DisplayedDetailType.CREATED_BY]: YDisplayedDetailType.CREATED_BY,
  [DisplayedDetailType.LAST_UPDATED_AT]: YDisplayedDetailType.LAST_UPDATED_AT,
  [DisplayedDetailType.LAST_UPDATED_BY]: YDisplayedDetailType.LAST_UPDATED_BY,
  [DisplayedDetailType.EXCERPT_CONTENT]: YDisplayedDetailType.EXCERPT_CONTENT,
  [DisplayedDetailType.VERSION_NUMBER]: YDisplayedDetailType.VERSION_NUMBER,
};

export const YDisplayedDetailTypeMap: Record<YDisplayedDetailType, DisplayedDetailType> =
  {
    [YDisplayedDetailType.CREATED_AT]: DisplayedDetailType.CREATED_AT,
    [YDisplayedDetailType.CREATED_BY]: DisplayedDetailType.CREATED_BY,
    [YDisplayedDetailType.LAST_UPDATED_AT]: DisplayedDetailType.LAST_UPDATED_AT,
    [YDisplayedDetailType.LAST_UPDATED_BY]: DisplayedDetailType.LAST_UPDATED_BY,
    [YDisplayedDetailType.EXCERPT_CONTENT]: DisplayedDetailType.EXCERPT_CONTENT,
    [YDisplayedDetailType.VERSION_NUMBER]: DisplayedDetailType.VERSION_NUMBER,
  };

export const BacklinkDisplayTypeMap: Record<BacklinkDisplayType, YBacklinkDisplayType> = {
  [BacklinkDisplayType.AS_LINK]: YBacklinkDisplayType.AS_LINK,
  [BacklinkDisplayType.AS_NUMBER]: YBacklinkDisplayType.AS_NUMBER,
};

export const YBacklinkDisplayTypeMap: Record<YBacklinkDisplayType, BacklinkDisplayType> =
  {
    [YBacklinkDisplayType.AS_LINK]: BacklinkDisplayType.AS_LINK,
    [YBacklinkDisplayType.AS_NUMBER]: BacklinkDisplayType.AS_NUMBER,
  };

export const ADSRefresh2025Colors = [
  'limeDark',
  'magentaDark',
  'orangeDark',
  'limeLight',
  'magentaLight',
  'orangeLight',
];

export const SelectOptionColors = [
  'blueDark',
  'redDark',
  'yellowDark',
  'greenDark',
  'tealDark',
  'purpleDark',
  'greyDark',
  'blueLight',
  'redLight',
  'yellowLight',
  'greenLight',
  'tealLight',
  'purpleLight',
  'greyLight',

  // new colors with ADS refresh
  ...ADSRefresh2025Colors,
] as const;

export const darkSelectOptionColors = SelectOptionColors.filter((c) =>
  c.endsWith('Dark')
);
export const lightSelectOptionColors = SelectOptionColors.filter((c) =>
  c.endsWith('Light')
);

export type SelectOptionColor = (typeof SelectOptionColors)[number];

export type SelectOptionJSON = {
  id: string;
  label: string;
  color: SelectOptionColor;
};

export interface SelectOption {
  get id(): string;
  get label(): string;
  get color(): SelectOptionColor;
  toJSON(): SelectOptionJSON;
}

export interface MutableSelectOption extends SelectOption {
  applyLabelDelta(delta: Op[]): void;
  set color(color: SelectOptionColor);
}

export interface SelectOptions {
  get options(): SelectOption[];
  toJSON(): SelectOptionJSON[];
}

export interface MutableSelectOptions extends SelectOptions {
  get options(): MutableSelectOption[];
  add(option: SelectOptionJSON): void;
  reorder(index: number, newIndex: number): void;
  delete(index: number): void;
}

export interface Field {
  get defaultEnabled(): boolean;
  get description(): string;
  get multi(): boolean;
  get name(): string;
  get showDescription(): boolean;
  get type(): FieldType;
  get width(): number | undefined;

  // TODO: move into field type interfaces
  get resolve(): boolean;
  get allowEdit(): boolean;
  get databaseId(): string | undefined;
  get displayedDetail(): DisplayedDetailType;
  get backlinkDisplayType(): BacklinkDisplayType;
  get fieldId(): string | undefined;
  get includeVersionComment(): boolean | undefined;
  get creatable(): boolean | undefined;
  get entryFieldField(): string | undefined;
  get pageTemplateId(): string | undefined;
  get pageSubType(): PageSubType;
  get parentPage(): string | undefined;
  get parentPageOption(): ParentPageOption;
  get isParentPageSourceDatabase(): boolean | undefined;
  get displayEntryOnPage(): boolean | undefined;
  get parentPageType(): ParentPageType;
  get jiraField(): string | undefined;
  get jiraServerId(): string | undefined;
  get cloudId(): string | undefined;
  get numberFormat(): NumberFormat;
  get customNumberFormat(): CustomNumberFormat;
  get mediaFormat(): MediaFormat;
  get selectOptions(): SelectOptions;
  get mentionNotificationEnabled(): boolean;
  get dateFormat(): DateFormat | undefined;
  get timeFormat(): TimeFormat | undefined;
  get enableDateRange(): boolean | undefined;
  toJSON(): FieldJSON;
}

export type FieldJSON = {
  description: string;
  defaultEnabled: boolean;
  name: string;
  multi: boolean;
  showDescription: boolean;
  type: FieldType;
  width: number | undefined;

  // TODO: move into field type interfaces
  resolve: boolean;
  allowEdit: boolean;
  databaseId: string | undefined;
  displayedDetail: DisplayedDetailType | undefined;
  contentField: string | undefined;
  backlinkDisplayType: BacklinkDisplayType;
  fieldId: string | undefined;
  includeVersionComment: boolean | undefined;
  creatable: boolean | undefined;
  entryFieldField: string | undefined;
  pageTemplateId: string | undefined;
  pageSubType: PageSubType;
  parentPage: string | undefined;
  parentPageOption: ParentPageOption;
  isParentPageSourceDatabase: boolean | undefined;
  displayEntryOnPage: boolean | undefined;
  parentPageType: ParentPageType;
  jiraField: string | undefined;
  jiraServerId: string | undefined;
  cloudId: string | undefined;
  numberFormat: NumberFormat;
  mediaFormat: MediaFormat;
  customNumberFormat: CustomNumberFormatJSON;
  selectOptions: SelectOptionJSON[];
  mentionNotificationEnabled: boolean;
  dateFormat: DateFormat | undefined;
  timeFormat: TimeFormat | undefined;
  enableDateRange: boolean | undefined;
};

export interface MutableField extends Field {
  applyDescriptionDelta(delta: Op[]): void;
  applyNameDelta(delta: Op[]): void;
  set defaultEnabled(val: boolean);
  set multi(val: boolean);
  set showDescription(val: boolean);
  set type(newType: FieldType);
  set width(val: number | undefined);

  // TODO: move into field type interfaces
  set resolve(val: boolean);
  set allowEdit(val: boolean);
  set displayedDetail(val: DisplayedDetailType);
  set contentField(val: string | undefined);
  set backlinkDisplayType(val: BacklinkDisplayType);
  set databaseId(val: string | undefined);
  set fieldId(val: string | undefined);
  set includeVersionComment(val: boolean | undefined);
  set creatable(val: boolean | undefined);
  set entryFieldField(val: string | undefined);
  set pageTemplateId(val: string | undefined);
  set pageSubType(val: PageSubType);
  set parentPage(val: string | undefined);
  set parentPageOption(val: ParentPageOption);
  set isParentPageSourceDatabase(val: boolean | undefined);
  set displayEntryOnPage(val: boolean | undefined);
  set parentPageType(val: ParentPageType);
  set jiraField(val: string | undefined);
  set jiraServerId(val: string | undefined);
  set cloudId(val: string | undefined);
  set numberFormat(val: NumberFormat);
  set mediaFormat(val: MediaFormat);
  get customNumberFormat(): MutableCustomNumberFormat;
  get selectOptions(): MutableSelectOptions;
  set mentionNotificationEnabled(val: boolean);
  set dateFormat(val: DateFormat | undefined);
  set timeFormat(val: TimeFormat | undefined);
  set enableDateRange(val: boolean | undefined);
}
